import { get, isEqual, keyBy, orderBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { v4 as uuid } from 'uuid';
import { RuntimeContext } from '../../../../../common/RuntimeContext';
import Page from '../../../../shared/components/Page';
import PaginatedFetch from '../../../../shared/components/PaginatedFetch';
import VerbTable from '../../../../shared/components/VerbTable';
import { LEARNING_ASSET_TYPES } from '../../../../shared/Constants';
import GenericErrorPage from '../../../../shared/containers/GenericErrorPage';
import { getReflectionDefaults, getTakeActionDefaults } from '../../../../shared/utils/LearningAssetUtils';
import SearchFormV2 from '../../../components/SearchFormV2';
import LearningAssetTableRow from './LearningAssetTableRow';
import LearningAssetTableSearchFilter from './LearningAssetTableSearchFilter';
import ManageTableButtonSection from './ManageTableButtonSection';

const fetchLimit = 200;

class LearningAssetTableV2 extends Component {
    static propTypes = {
        history: PropTypes.object,
        getLearningAsset: PropTypes.func,
        getWelcomeAsset: PropTypes.func,
        loggedInUser: PropTypes.object,
        getLearningAssetStatus: PropTypes.func,
        updateLearningAssetArchiveStatus: PropTypes.func,
        isVerbAdmin: PropTypes.bool,
        isContentAdmin: PropTypes.bool,
        isContentAuthor: PropTypes.bool,
        getManageLearningAssetsTableData: PropTypes.func,
        isWelcomeAssetTable: PropTypes.bool,
        isGlobalSectionItemUsageTable: PropTypes.bool,
        hideCreatedByAndAuthorColumns: PropTypes.bool,
        learningAssetTableDataList: PropTypes.array,
        postAlgoliaSearch: PropTypes.func,
        updateAlgoliaSearchFilters: PropTypes.func,
        updateAlgoliaSearchQuery: PropTypes.func,
        initializeAlgoliaSearchState: PropTypes.func,
        clearAlgoliaSearchResult: PropTypes.func,
        algoliaSearch: PropTypes.object
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);

        this.state = {
            page: 1,
            noSearchResult: false,
            showNoActivityMessage: true,
            sortField: 'updatedOn',
            sortOrder: -1, // desc
            learningAssetTableData: [],
            learningAssetTableSearchData: [],
            allLearningAssetsById: [],
            hasError: false
        };
    }

    componentDidMount() {
        const { sortField, sortOrder } = this.state;
        const { isWelcomeAssetTable, initializeAlgoliaSearchState } = this.props;

        const params = { limit: fetchLimit, page: 1, sortField, sortOrder };
        if (isWelcomeAssetTable) {
            params.getWelcomeAssets = true;
            this.algoliaIndex = 'WelcomeAssets';
        } else {
            this.algoliaIndex = 'LearningAssets';
        }

        initializeAlgoliaSearchState({
            pageName: 'ManageLearningAssets',
            initialState: {
                indices: this.algoliaIndex,
                filters: {}
            },
            pathsContext: this.context.pathsWeb
        });

        this.getLearningAssetsTableData(params);
    }

    componentDidUpdate(prevProps) {
        const { algoliaSearch = {} } = this.props;
        const prevAlgoliaSearch = prevProps.algoliaSearch || {};
        
        const newAlgoliaSearchResults = get(algoliaSearch, `searchResults.${this.algoliaIndex}`, []);
        const prevAlagoliaSearchResults = get(prevAlgoliaSearch, `searchResults.${this.algoliaIndex}`, []);
        
        const newFilters = get(algoliaSearch, 'filters', {});
        const prevFilters = get(prevAlgoliaSearch, 'filters', {});
        
        // Update search data if results changed, noResults changed, or filters changed
        if (
            !isEqual(prevAlagoliaSearchResults, newAlgoliaSearchResults) ||
            prevAlgoliaSearch.noResults !== algoliaSearch.noResults ||
            !isEqual(prevFilters, newFilters)
        ) {
            const algoliaSearchResults = newAlgoliaSearchResults || [];
            // Show all results regardless of author
            this.setState({
                learningAssetTableSearchData: algoliaSearchResults
            });
        }
    }

    static getDerivedStateFromError(err) {
        console.log(err);
        if (err.name === 'TypeError' || err.name === 'ReferenceError') {
            return { hasError: true };
        }
        return { hasError: false };
    }

    static getDerivedStateFromProps(props, state) {
        if (
            props.isGlobalSectionItemUsageTable &&
            state.learningAssetTableData.length !== props.learningAssetTableDataList.length
        ) {
            return {
                learningAssetTableData: props.learningAssetTableDataList
            };
        }
        return null;
    }

    getLearningAssetsTableData = (params) => {
        const {
            getManageLearningAssetsTableData,
            isGlobalSectionItemUsageTable,
            learningAssetTableDataList
        } = this.props;
        this.setState({ showNoActivityMessage: false });
        if (isGlobalSectionItemUsageTable) {
            return this.setState({ learningAssetTableData: learningAssetTableDataList });
        }
        return getManageLearningAssetsTableData({ params, pathsContext: this.context.pathsWeb })
            .then((tableData) => {
                const allLearningAssetsById = keyBy(tableData, (asset) => asset._id);
                this.setState({
                    learningAssetTableData: tableData,
                    showNoActivityMessage: true,
                    allLearningAssetsById
                });
                return tableData;
            })
            .catch((err) => console.log(err));
    };

    _filterLearningAssetsByAuthor(unfilteredAssets) {
        const {
            loggedInUser: { _id: userId }
        } = this.props;
        const { allLearningAssetsById } = this.state;
        return unfilteredAssets.filter((asset) => {
            // First try to get author from the asset itself (Algolia result)
            if (asset.author && asset.author._id) {
                return asset.author._id === userId;
            }
            // Fallback to looking up in allLearningAssetsById
            return allLearningAssetsById[asset._id]?.author?._id === userId;
        });
    }

    _getAsset = ({ type, _id }) => {
        const { getLearningAsset, getWelcomeAsset } = this.props;
        return type === 'WELCOME'
            ? getWelcomeAsset({ welcomeAssetId: _id })
            : getLearningAsset({ learningAssetId: _id, pathsContext: this.context.pathsWeb });
    };

    _onClickHandler = (learningAsset) => {
        const { history, loggedInUser } = this.props;
        const assetType = learningAsset.type === 'WELCOME' ? 'welcome' : 'learning';
        return this._getAsset(learningAsset).then((fullLearningAsset) => {
            const copiedObject = JSON.parse(JSON.stringify(fullLearningAsset));
            const learningAssetId = copiedObject._id;
            delete copiedObject._id;
            delete copiedObject.revision;
            delete copiedObject.updatedOn;
            delete copiedObject.updatedOn;
            copiedObject.isActive = false;
            copiedObject.name = `Copy of ${copiedObject.name}`;
            copiedObject.author = {
                _id: loggedInUser._id,
                firstName: loggedInUser.firstName,
                lastName: loggedInUser.lastName
            };
            const idDictionary = {};
            const sections = copiedObject.sections
                ? copiedObject.sections.map((section) => {
                      const newId = uuid();
                      idDictionary[section.id] = newId;
                      section.id = newId;
                      section.sectionItems = (section.sectionItems || []).map((sectionItem) => {
                          const newId = uuid();
                          idDictionary[sectionItem.id] = newId;
                          const newSectionItem = {
                              ...sectionItem,
                              id: newId,
                              sectionId: section.id
                          };
                          if (
                              (sectionItem.type === 'Show Previous Answers' ||
                                  sectionItem.type === 'Show Previous Answers and Choose One') &&
                              sectionItem.content &&
                              sectionItem.content.questionsToDisplay
                          ) {
                              newSectionItem.content = { ...sectionItem.content };
                              newSectionItem.content.questionsToDisplay = sectionItem.content.questionsToDisplay.map(
                                  (question) => {
                                      question.questionId = idDictionary[question.questionId];
                                      return question;
                                  }
                              );
                          }
                          if (
                              sectionItem.type === 'Short Essay Results' &&
                              sectionItem.content &&
                              sectionItem.content.items
                          ) {
                              newSectionItem.content = { ...sectionItem.content };
                              newSectionItem.content.items = sectionItem.content.items.map((question) => {
                                  question.itemId = idDictionary[question.itemId];
                                  return question;
                              });
                          }
                          if (
                              (sectionItem.type === 'This or That Chart' ||
                                  sectionItem.type === 'This or That Results') &&
                              sectionItem.content &&
                              sectionItem.content.questionSectionItemId
                          ) {
                              newSectionItem.content = { ...sectionItem.content };
                              newSectionItem.content.questionSectionItemId =
                                  idDictionary[sectionItem.content.questionSectionItemId];
                          }
                          return newSectionItem;
                      });
                      return section;
                  })
                : [];
            copiedObject.sections = sections;
            return history.push(`/new${assetType}asset/summary`, {
                type: copiedObject.type,
                existingObject: copiedObject,
                isCopied: true,
                learningAssetId
            });
        });
    };

    /**
     *
     * return which data set the table should be reading/modifying
     *
     * @return {String}
     */
    _getCurrentTableData = () => {
        const { learningAssetTableSearchData } = this.state;
        return learningAssetTableSearchData.length ? 'learningAssetTableSearchData' : 'learningAssetTableData';
    };

    _updateLearningAssetArchiveStatus = (params) =>
        this.props.updateLearningAssetArchiveStatus(params).then((learningAsset) => {
            const tableDataToUpdate = this._getCurrentTableData();

            const newTableData = [...this.state[tableDataToUpdate]];
            for (let i = 0; i <= newTableData.length; i += 1) {
                if (newTableData[i]._id === learningAsset._id) {
                    newTableData.splice(i, 1);
                    break;
                }
            }
            this.setState({
                [tableDataToUpdate]: newTableData
            });
        });

    /**
     *
     * @param {String} type
     * @param {string} [initialSectionItemType=null]
     * @private
     */
    onNewItemClick = (type, initialSectionItemType = null) => {
        let stateObj = {
            type
        };
        let initialSection;

        // build initial sections for article and video type
        if (type === LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION) {
            const initialSectionId = uuid();
            initialSection = getTakeActionDefaults(initialSectionId);
        } else if (type === LEARNING_ASSET_TYPES.REFLECTION) {
            const initialSectionId = uuid();
            initialSection = getReflectionDefaults(initialSectionId);
        } else if (initialSectionItemType) {
            const initialSectionId = uuid();
            initialSection = {
                totalSectionItemCount: 1,
                sections: [
                    {
                        id: initialSectionId,
                        sectionIcon: 'asterisk',
                        name: 'New Section',
                        sectionItems: [
                            {
                                sectionId: initialSectionId,
                                name: 'New Section Item',
                                id: uuid(),
                                isActive: true,
                                type: initialSectionItemType,
                                startsAfter: 'start',
                                startsOn: 1,
                                targetParticipantRole: 'all'
                            }
                        ]
                    }
                ]
            };
        }
        stateObj = Object.assign(stateObj, initialSection);

        const path = type === LEARNING_ASSET_TYPES.WELCOME ? 'welcome' : 'learning';
        this.props.history.push(`/new${path}asset/summary`, stateObj);
    };

    _handleColumnSorting = (sortField) => {
        const tableDataToSort = this._getCurrentTableData();
        const sortOrder = this.state.sortField === sortField ? this.state.sortOrder * -1 : -1;

        const parsedSortOrder = sortOrder === 1 ? 'asc' : 'desc';

        this.setState({
            [tableDataToSort]: [...orderBy(this.state[tableDataToSort], sortField, parsedSortOrder)],
            sortField,
            sortOrder
        });
    };

    _renderLearningAssetTable = () => {
        const {
            getLearningAssetStatus,
            isVerbAdmin,
            isContentAdmin,
            history,
            isWelcomeAssetTable,
            isGlobalSectionItemUsageTable,
            hideCreatedByAndAuthorColumns,
            algoliaSearch = {}
        } = this.props;
        const { showNoActivityMessage } = this.state;

        const { learningAssetTableData, learningAssetTableSearchData } = this.state;
        const sortFieldsByHeaderNames = {
            ACTIONS: '',
            NAME: 'name',
            PATHS: 'Paths',
            TYPE: 'type',
            'CREATED BY': 'createdBy',
            AUTHOR: learningAssetTableSearchData && learningAssetTableSearchData.length ? 'author' : 'author.lastName',
            'UPDATED ON': 'updatedOn',
            'IS ACTIVE?': 'isActive',
            'HAS COMMENT?': 'comments'
        };

        if (isWelcomeAssetTable || isGlobalSectionItemUsageTable) {
            delete sortFieldsByHeaderNames.PATHS;
            delete sortFieldsByHeaderNames['CREATED BY'];
            delete sortFieldsByHeaderNames['HAS COMMENT?'];
        }

        if (hideCreatedByAndAuthorColumns) {
            delete sortFieldsByHeaderNames['CREATED BY'];
            delete sortFieldsByHeaderNames.AUTHOR;
        }
        let tableData = [];
        if ((learningAssetTableSearchData && learningAssetTableSearchData.length) || algoliaSearch.noResults) {
            tableData = learningAssetTableSearchData;
        } else {
            // Fall back to the initial table data when search results are empty.
            tableData = learningAssetTableData;
        }
        
        if (!tableData.length && isGlobalSectionItemUsageTable) {
            return <p>This global section item is not associated with any assets</p>;
        }

        const tableHeader = (
            <thead>
                <tr>
                    {Object.keys(sortFieldsByHeaderNames).map((headerName) => (
                        <th key={headerName}>
                            <span
                                onClick={
                                    sortFieldsByHeaderNames[headerName]
                                        ? () => {
                                              this._handleColumnSorting(sortFieldsByHeaderNames[headerName]);
                                          }
                                        : null
                                }
                                style={sortFieldsByHeaderNames[headerName] ? { cursor: 'pointer' } : {}}
                            >
                                {headerName}
                            </span>
                        </th>
                    ))}
                </tr>
            </thead>
        );

        return (
            <>
                <VerbTable className={'manage-table'} headerComponent={tableHeader} isHover>
                    <tbody>
                        {tableData.length
                            ? tableData.map((asset) => (
                                  <LearningAssetTableRow
                                      getLearningAssetStatus={getLearningAssetStatus}
                                      key={asset._id || asset.objectId}
                                      learningAsset={asset}
                                      isVerbAdmin={isVerbAdmin}
                                      isContentAdmin={isContentAdmin}
                                      history={history}
                                      updateLearningAssetArchiveStatus={this._updateLearningAssetArchiveStatus}
                                      cloneLearningAsset={this._onClickHandler}
                                      isWelcomeAssetTable={isWelcomeAssetTable}
                                      isGlobalSectionItemUsageTable={isGlobalSectionItemUsageTable}
                                      hideCreatedByAndAuthorColumns={hideCreatedByAndAuthorColumns}
                                  />
                              ))
                            : null}
                    </tbody>
                </VerbTable>

                {!tableData.length && showNoActivityMessage && (
                    <div className={'no-activities-message'}>
                        {algoliaSearch.noResults
                            ? 'No search results.'
                            : 'You haven’t made any activities yet. Click New Activity above to get started.'}
                    </div>
                )}
            </>
        );
    };

    getMoreLearningAssets = () => {
        const { page, sortField, sortOrder } = this.state;

        return this.props
            .getManageLearningAssetsTableData({
                limit: fetchLimit,
                page: page + 1,
                sortField,
                sortOrder,
                pathsContext: this.context.pathsWeb
            })
            .then((learningAssets) => {
                const currentLearningAssetTableDataById = keyBy(this.state.learningAssetTableData, '_id');
                const dedupedLearningAssets = learningAssets.filter(
                    (asset) => !currentLearningAssetTableDataById[asset._id]
                ); // filter out duplicate learningAssets
                const moreAllLearningAssetsById = keyBy(dedupedLearningAssets, (asset) => asset._id);
                this.setState({
                    page: this.state.page + 1,
                    learningAssetTableData: [...this.state.learningAssetTableData, ...dedupedLearningAssets],
                    allLearningAssetsById: { ...this.state.allLearningAssetsById, ...moreAllLearningAssetsById }
                });
                return learningAssets; // must return learningAssets
            });
    };

    _renderLoadMoreButton = () => {
        const { algoliaSearch = {} } = this.props;
        const { learningAssetTableData, learningAssetTableSearchData } = this.state;
        if (get(algoliaSearch, `searchResults.${this.algoliaIndex}`, []).length || algoliaSearch.noResults) {
            return null;
        }

        if (learningAssetTableData && learningAssetTableData.length < fetchLimit) {
            return null;
        }

        // no pagination for search data
        if (learningAssetTableSearchData && learningAssetTableSearchData.length) {
            return null;
        }

        return <PaginatedFetch fetchMoreFunc={this.getMoreLearningAssets} limit={fetchLimit} />;
    };

    render() {
        const {
            isWelcomeAssetTable,
            isGlobalSectionItemUsageTable,
            isContentAuthor,
            clearAlgoliaSearchResult,
            postAlgoliaSearch,
            updateAlgoliaSearchQuery,
            updateAlgoliaSearchFilters,
            algoliaSearch,
            isVerbAdmin
        } = this.props;
        const { hasError } = this.state;
        if (hasError) {
            return (
                <Page className={'manage-learning-assets'}>
                    <GenericErrorPage />
                </Page>
            );
        }

        return (
            <div className={'learning-asset-table'}>
                {!isGlobalSectionItemUsageTable ? (
                    <>
                        <div className={'row'}>
                            <ManageTableButtonSection
                                onNewItemClick={this.onNewItemClick}
                                isWelcomeAssetTable={isWelcomeAssetTable}
                                hideForContentAuthor={isContentAuthor}
                            />
                            <div className={'col-6 ms-auto'} style={{ marginBottom: '15px' }}>
                                <SearchFormV2
                                    postAlgoliaSearch={postAlgoliaSearch}
                                    algoliaSearch={algoliaSearch}
                                    updateAlgoliaSearchQuery={updateAlgoliaSearchQuery}
                                    clearAlgoliaSearchResult={clearAlgoliaSearchResult}
                                    algoliaIndex={this.algoliaIndex}
                                    pathsContext={this.context.pathsWeb}
                                />
                            </div>
                        </div>
                        <br />
                    </>
                ) : null}
                {!isWelcomeAssetTable && (
                    <LearningAssetTableSearchFilter
                        algoliaSearch={algoliaSearch}
                        postAlgoliaSearch={postAlgoliaSearch}
                        updateAlgoliaSearchFilters={updateAlgoliaSearchFilters}
                        clearAlgoliaSearchResult={clearAlgoliaSearchResult}
                        algoliaIndex={this.algoliaIndex}
                        isVerbAdmin={isVerbAdmin}
                    />
                )}
                {this._renderLearningAssetTable()}
                {!isGlobalSectionItemUsageTable ? this._renderLoadMoreButton() : null}
            </div>
        );
    }
}

export default LearningAssetTableV2;
